import * as React from "react";
import { useState, useEffect, useCallback } from "react";
// Import Comlink for web worker communication
import { wrap } from "comlink";
// Import Sentry for error reporting
import * as Sentry from "@sentry/browser";

/**
 * Notice component for displaying site-wide announcements or important messages
 * 
 * Types: "info" (default), "warning", "success", "error"
 * 
 * Can display notices from:
 * 1. Local storage (persistent notices)
 * 2. Custom events (programmatically triggered)
 * 3. Firebase collection (centrally managed)
 */
const Notice = () => {
  // State to track if there's an active notice to display
  const [active, setActive] = useState(false);
  // Notice content and type
  const [notice, setNotice] = useState({
    message: "",
    type: "info", // info, warning, success, error
    dismissible: true,
    autoHide: 0, // 0 means don't auto-hide, otherwise time in ms
    id: null // Firebase document ID if from Firestore
  });
  // Track if we've checked Firebase
  // eslint-disable-next-line no-unused-vars
  const [checkedFirebase, setCheckedFirebase] = useState(false);
  // Track if component is mounted to prevent state updates after unmount
  const [isMounted, setIsMounted] = useState(true);
  // Use ref to track the auto-hide timeout
  const autoHideTimeoutRef = React.useRef(null);

  // Check if a notice has been dismissed using the web worker
  const checkIfNoticeDismissed = useCallback(async (noticeId) => {
    if (!noticeId) return false;
    
    // Skip if cleanup is in progress
    if (typeof window !== 'undefined' && window._firebaseCleanupInProgress) {
      return false;
    }
    
    try {
      // Initialize web worker
      const worker = new Worker("/workers/main.js");
      const obj = wrap(worker);
      
      // Add worker to tracking array for cleanup
      if (typeof window !== 'undefined') {
        window._activeFirebaseWorkers.push(worker);
      }
      
      // Check if notice has been dismissed
      const isDismissed = await obj.isNoticeDismissed({ noticeId });
      
      // Terminate worker when done
      worker.terminate();
      
      // Remove from tracking array
      if (typeof window !== 'undefined' && window._activeFirebaseWorkers) {
        const index = window._activeFirebaseWorkers.indexOf(worker);
        if (index > -1) {
          window._activeFirebaseWorkers.splice(index, 1);
        }
      }
      
      return isDismissed;
    } catch (error) {
      // Error will be reported to Sentry
      Sentry.withScope((scope) => {
        scope.setTag('component', 'Notice');
        scope.setTag('function', 'checkIfNoticeDismissed');
        scope.setLevel('error');
        Sentry.captureException(error);
      });
      
      return false;
    }
  }, []);

  // Handle dismissing the notice
  const handleDismiss = useCallback(async () => {
    // First, immediately remove from localStorage and hide the notice
    // This ensures the notice is dismissed even if the Firebase operation fails
    if (notice.persistent) {
      try {
        localStorage.removeItem('site-notice');
      } catch (error) {
        console.error('Error removing notice from localStorage:', error);
      }
    }
    
    // Store the dismissed notice ID in localStorage to prevent it from reappearing
    if (notice.id) {
      try {
        // 1. Store individual notice dismissal record
        const dismissalRecord = {
          id: notice.id,
          dismissedAt: Date.now()
        };
        localStorage.setItem(`dismissed-notice-${notice.id}`, JSON.stringify(dismissalRecord));
        
        // 2. Update master list of dismissed notices
        let dismissedNotices = [];
        try {
          const existingList = localStorage.getItem('dismissed-notices');
          if (existingList) {
            dismissedNotices = JSON.parse(existingList);
          }
        } catch (_e) {
          // If parsing fails, start with empty array
          dismissedNotices = [];
        }
        
        // Add to list if not already present
        if (!dismissedNotices.includes(notice.id)) {
          dismissedNotices.push(notice.id);
          localStorage.setItem('dismissed-notices', JSON.stringify(dismissedNotices));
        }
      } catch (error) {
        console.error('Error storing dismissed notice record:', error);
      }
    }
    
    // Hide the notice immediately
    setActive(false);
    
    // If notice has an ID, mark it as dismissed in Firebase (but don't block UI on this)
    if (notice.id) {
      // Skip if cleanup is in progress
      if (typeof window !== 'undefined' && window._firebaseCleanupInProgress) {
        return;
      }
      
      // Use a try/finally to ensure worker is cleaned up even if dismissal fails
      let worker = null;
      try {
        // Initialize web worker
        worker = new Worker("/workers/main.js");
        const obj = wrap(worker);
        
        // Add worker to tracking array for cleanup
        if (typeof window !== 'undefined') {
          if (!window._activeFirebaseWorkers) {
            window._activeFirebaseWorkers = [];
          }
          window._activeFirebaseWorkers.push(worker);
        }
        
        // Mark notice as dismissed
        await obj.dismissNotice({ noticeId: notice.id });
      } catch (error) {
        // Report error to Sentry
        Sentry.withScope((scope) => {
          scope.setTag('component', 'Notice');
          scope.setTag('function', 'handleDismiss');
          scope.setLevel('error');
          Sentry.captureException(error);
        });
      } finally {
        // Always clean up the worker
        if (worker) {
          try {
            worker.terminate();
          } catch (_e) {
            // Silent error handling for worker termination
          }
          
          // Remove from tracking array
          if (typeof window !== 'undefined' && window._activeFirebaseWorkers) {
            const index = window._activeFirebaseWorkers.indexOf(worker);
            if (index > -1) {
              window._activeFirebaseWorkers.splice(index, 1);
            }
          }
        }
      }
    }
  }, [notice]);

  // Handle showing a notice from an event
  const handleShowNotice = useCallback((event) => {
    // Don't update state if component is unmounted
    if (!isMounted) return;
    if (event && event.detail) {
      const { message, type = "info", dismissible = true, autoHide = 0, persistent = false, id = null, priority = 1, startDate, endDate } = event.detail;
      
      // Skip if no message
      if (!message) return;
      
      // Check date range if provided
      const now = new Date();
      if (startDate && new Date(startDate) > now) return;
      if (endDate && new Date(endDate) < now) return;
      
      // If there's already an active notice with higher priority, don't override it
      if (active && notice.priority && notice.priority > priority) return;
      
      // Set the new notice
      setNotice({
        message,
        type,
        dismissible,
        autoHide,
        persistent,
        id,
        priority
      });
      
      // Store in localStorage if persistent
      if (persistent) {
        try {
          const storageData = {
            message,
            type,
            dismissible,
            persistent,
            id,
            priority,
            timestamp: Date.now()
          };
          
          // Add expiry if endDate is provided
          if (endDate) {
            storageData.expiry = new Date(endDate).getTime();
          }
          
          localStorage.setItem("site-notice", JSON.stringify(storageData));
        } catch (e) {
          // Error will be reported to Sentry
          Sentry.withScope(scope => {
            scope.setTag('component', 'Notice');
            scope.setTag('function', 'handleShowNotice');
            scope.setLevel('error');
            Sentry.captureException(e);
          });
        }
      }
      
      // Show the notice with a slight delay for smoother appearance
      setTimeout(() => {
        if (isMounted) {
          setActive(true);
        }
      }, 10);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, notice]);

  // Fetch notices from Firebase
  // Function to clean up Firebase connections before navigation
  const cleanupFirebaseConnections = useCallback(() => {
    if (typeof window === 'undefined') return;
    
    // Set cleanup flag to prevent new Firebase operations
    window._firebaseCleanupInProgress = true;
    
    // Terminate any active Firebase workers
    if (window._activeFirebaseWorkers && window._activeFirebaseWorkers.length > 0) {
      // Clean up active Firebase workers before navigation
      
      // Terminate each worker
      window._activeFirebaseWorkers.forEach(worker => {
        try {
          worker.terminate();
        } catch (_error) {
          // Silent error handling for worker termination
        }
      });
      
      // Clear the workers array
      window._activeFirebaseWorkers = [];
    }
  }, []);

  const fetchFirebaseNotices = useCallback(async () => {
    // Define worker variable at the function scope level so it can be accessed in finally block
    let worker;
    
    try {
      // Only run in browser
      if (typeof window === 'undefined') {
        setCheckedFirebase(true);
        return;
      }
      
      // Skip if cleanup is in progress
      if (typeof window !== 'undefined' && window._firebaseCleanupInProgress) {
        setCheckedFirebase(true);
        return;
      }
      
      // Dynamically import Firebase to avoid SSR issues
      const getFirebase = (await import('../../firebaseConfig')).default;
      const { getFirestore, collection, query, where, getDocs, orderBy, limit } = await import('firebase/firestore');
      
      try {
        // Initialize Firebase using the project's getFirebase function
        const app = getFirebase();
        
        if (!app) {
          // Firebase not initialized, browser may not be available
          setCheckedFirebase(true);
          return;
        }
        
        // Create a worker for Firebase operations
        worker = new Worker("/workers/main.js");
        
        // Add worker to tracking array for cleanup
        if (typeof window !== 'undefined') {
          window._activeFirebaseWorkers.push(worker);
        }
        
        const db = getFirestore();
        
        // Query active notices
        const now = new Date();
        
        try {
          // First, try a simple query to see if we can access the collection at all
          const noticesCollection = collection(db, 'notices');
          // Just check if we can access the collection without storing the result
          await getDocs(noticesCollection);
          
          // Execute Firestore query
          
          // Process the documents returned
          let activeNotices = [];
          
          try {
            // Try the full query with filters and orderBy
            const noticesQuery = query(
              collection(db, 'notices'),
              where('active', '==', true),
              where('startDate', '<=', now),
              where('endDate', '>=', now),
              orderBy('priority', 'desc'),
              limit(1)
            );
            
            const querySnapshot = await getDocs(noticesQuery);
            
            if (!querySnapshot.empty) {
              const doc = querySnapshot.docs[0];
              const noticeData = doc.data();
              // Check if notice has been dismissed
              const isDismissed = await checkIfNoticeDismissed(doc.id);
              
              if (noticeData.active && 
                  (!noticeData.startDate || noticeData.startDate.toDate() <= now) && 
                  (!noticeData.endDate || noticeData.endDate.toDate() >= now) && 
                  !isDismissed) {
                  
                activeNotices.push({
                  id: doc.id,
                  message: noticeData.message,
                  type: noticeData.type || 'info',
                  dismissible: noticeData.dismissible !== false,
                  autoHide: noticeData.autoHide || 0,
                  persistent: noticeData.persistent || false,
                  priority: noticeData.priority || 1
                });
              }
            }
          } catch (error) {
            // Complex query failed, try a simpler query
            
            // Report to Sentry
            Sentry.withScope(scope => {
              scope.setTag('component', 'Notice');
              scope.setTag('function', 'fetchFirebaseNotices');
              scope.setTag('query', 'complex');
              scope.setLevel('warning');
              Sentry.captureException(error);
            });
            
            // Try a simpler query without the complex filters
            try {
              const simpleQuery = query(
                collection(db, 'notices'),
                where('active', '==', true),
                orderBy('priority', 'desc'),
                limit(5)
              );
              
              const simpleSnapshot = await getDocs(simpleQuery);
              
              // Process results manually
              for (const doc of simpleSnapshot.docs) {
                const noticeData = doc.data();
                const isDismissed = await checkIfNoticeDismissed(doc.id);
                
                // Manual date checking
                const startDate = noticeData.startDate ? noticeData.startDate.toDate() : null;
                const endDate = noticeData.endDate ? noticeData.endDate.toDate() : null;
                
                if (noticeData.active && 
                    (!startDate || startDate <= now) && 
                    (!endDate || endDate >= now) && 
                    !isDismissed) {
                    
                  activeNotices.push({
                    id: doc.id,
                    message: noticeData.message,
                    type: noticeData.type || 'info',
                    dismissible: noticeData.dismissible !== false,
                    autoHide: noticeData.autoHide || 0,
                    persistent: noticeData.persistent || false,
                    priority: noticeData.priority || 1
                  });
                }
              }
            } catch (simpleQueryError) {
              // Simple query also failed
              console.error('Simple query also failed', simpleQueryError);
              
              // Report error to Sentry with context
              Sentry.withScope((scope) => {
                scope.setTag('component', 'Notice');
                scope.setTag('query_type', 'simple_query');
                scope.setLevel('error');
                Sentry.captureException(simpleQueryError);
              });
            }
          }
          
          // If we found any active notices, use the highest priority one
          if (activeNotices.length > 0) {
            // Sort by priority
            activeNotices.sort((a, b) => (b.priority || 0) - (a.priority || 0));
            
            const topNotice = activeNotices[0];
            
            // Set the notice
            handleShowNotice({
              detail: topNotice
            });
          }
        } catch (error) {
          // Error accessing collection
          console.error('Error accessing Firestore collection', error);
          
          // Report to Sentry
          Sentry.withScope(scope => {
            scope.setTag('component', 'Notice');
            scope.setTag('function', 'fetchFirebaseNotices');
            scope.setTag('query', 'collection');
            scope.setLevel('error');
            Sentry.captureException(error);
          });
        }
      } catch (error) {
        // Error initializing Firebase
        console.error('Error initializing Firebase', error);
        
        // Report to Sentry
        Sentry.withScope(scope => {
          scope.setTag('component', 'Notice');
          scope.setTag('function', 'fetchFirebaseNotices');
          scope.setTag('step', 'initialization');
          scope.setLevel('error');
          Sentry.captureException(error);
        });
      }
    } catch (error) {
      // Error importing Firebase modules
      console.error('Error importing Firebase modules', error);
      
      // Report to Sentry
      Sentry.withScope(scope => {
        scope.setTag('component', 'Notice');
        scope.setTag('function', 'fetchFirebaseNotices');
        scope.setTag('step', 'import');
        scope.setLevel('error');
        Sentry.captureException(error);
      });
    } finally {
      // Mark that we've checked Firebase if component is still mounted
      if (isMounted) {
        setCheckedFirebase(true);
      }
      
      // Clean up the worker if it exists
      if (typeof window !== 'undefined' && window._activeFirebaseWorkers && worker) {
        try {
          worker.terminate();
          
          // Remove from tracking array
          const index = window._activeFirebaseWorkers.indexOf(worker);
          if (index > -1) {
            window._activeFirebaseWorkers.splice(index, 1);
          }
        } catch (error) {
          console.error('Error terminating worker in fetchFirebaseNotices:', error);
        }
      }
    }
  }, [checkIfNoticeDismissed, handleShowNotice, isMounted]);

  // Auto-hide timer with ref to prevent memory leaks
  useEffect(() => {
    if (active && notice.autoHide > 0) {
      // Clear any existing timeout
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }
      
      // Set new timeout
      autoHideTimeoutRef.current = setTimeout(() => {
        if (isMounted) {
          setActive(false);
        }
      }, notice.autoHide);
    }
    
    return () => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
        autoHideTimeoutRef.current = null;
      }
    };
  }, [active, notice.autoHide, isMounted]);

  // Set isMounted to false on unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);
  
  // Effect to check for notices on mount and handle cleanup events
  useEffect(() => {
    // Initialize worker tracking array if it doesn't exist
    if (typeof window !== 'undefined' && !window._activeFirebaseWorkers) {
      window._activeFirebaseWorkers = [];
    }
    
    // Initialize cleanup flag
    if (typeof window !== 'undefined') {
      window._firebaseCleanupInProgress = false;
    }
    
    // Check for notices in localStorage and Firebase
    const checkNotices = async () => {
      let hasActiveNotice = false;
      
      // Skip if cleanup is in progress
      if (typeof window !== 'undefined' && window._firebaseCleanupInProgress) {
        return;
      }
      
      // Check localStorage first
      const storedNotice = localStorage.getItem("site-notice");
      if (storedNotice) {
        try {
          const parsedNotice = JSON.parse(storedNotice);
          
          // Check if notice has expired
          if (parsedNotice.expiry && parsedNotice.expiry < Date.now()) {
            // Remove expired notice
            localStorage.removeItem("site-notice");
          } else {
            // First check local dismissal records
            let isDismissedLocally = false;
            
            if (parsedNotice.id) {
              // Check individual dismissal record
              const individualDismissalRecord = localStorage.getItem(`dismissed-notice-${parsedNotice.id}`);
              if (individualDismissalRecord) {
                isDismissedLocally = true;
              }
              
              // Also check master list of dismissed notices
              try {
                const dismissedList = localStorage.getItem('dismissed-notices');
                if (dismissedList) {
                  const dismissedIds = JSON.parse(dismissedList);
                  if (dismissedIds.includes(parsedNotice.id)) {
                    isDismissedLocally = true;
                  }
                }
              } catch (_e) {
                // If parsing fails, continue with individual check result
              }
            }
            
            // If not dismissed locally, check Firebase
            if (!isDismissedLocally) {
              const isDismissed = await checkIfNoticeDismissed(parsedNotice.id);
              
              if (!isDismissed) {
                // Show the stored notice
                handleShowNotice({
                  detail: parsedNotice
                });
                hasActiveNotice = true;
              }
            }
          }
        } catch (error) {
          console.error("Error parsing stored notice:", error);
          localStorage.removeItem("site-notice");
        }
      }
      
      // If no active notice from localStorage, check Firebase
      if (!hasActiveNotice) {
        await fetchFirebaseNotices();
      }
    };
    
    // Run the check
    checkNotices();
    
    // Set up global event listeners
    if (typeof window !== 'undefined') {
      window.addEventListener("show-notice", handleShowNotice);
      window.addEventListener("firebase-cleanup-before-navigation", cleanupFirebaseConnections);
    }
    
    return () => {
      // Remove event listeners
      if (typeof window !== 'undefined') {
        window.removeEventListener("show-notice", handleShowNotice);
        window.removeEventListener("firebase-cleanup-before-navigation", cleanupFirebaseConnections);
      }
      
      // Clean up any remaining workers on unmount
      cleanupFirebaseConnections();
    };
  }, [checkIfNoticeDismissed, handleShowNotice, fetchFirebaseNotices, cleanupFirebaseConnections]);

  // Determine background and text colors for maximum contrast
  const bgColors = {
    info: "bg-blue-900 border-blue-600 text-white font-bold",
    warning: "bg-amber-800 border-amber-500 text-white font-bold",
    success: "bg-green-900 border-green-600 text-white font-bold",
    error: "bg-red-900 border-red-600 text-white font-bold"
  };

  // Determine icon based on type
  const icons = {
    info: (
      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    ),
    warning: (
      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
    success: (
      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    ),
    error: (
      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    )
  };

  // Create a style tag to ensure high contrast text and smooth animations
  const styleContent = `
    .notice-alert {
      transition: all 0.3s ease-in-out;
      transform: translateY(0);
      opacity: 1;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      border-radius: 0.375rem;
    }
    
    .notice-content * {
      color: #FFFFFF !important;
      font-weight: bold !important;
      font-size: 1.1rem !important;
      letter-spacing: 0.02em !important;
    }
    
    .notice-dismiss-btn {
      transition: all 0.2s ease;
      opacity: 0.8;
    }
    
    .notice-dismiss-btn:hover {
      opacity: 1;
      transform: scale(1.1);
      background-color: rgba(255, 255, 255, 0.2);
    }
  `;

  // Only render the notice if it's active and has a message
  if (!active || !notice.message) {
    return null;
  }
  
  return (
    <div 
      className={`w-full px-6 py-5 mb-4 border-l-8 shadow-xl ${bgColors[notice.type] || bgColors.info} flex items-center justify-between notice-alert`}
      role="alert"
      aria-live="assertive"
      data-testid="notice-alert"
      data-notice-type={notice.type || 'info'}
    >
      <style dangerouslySetInnerHTML={{ __html: styleContent }} />
      <div className="flex items-center">
        <div className="flex-shrink-0">
          {icons[notice.type] || icons.info}
        </div>
        <div 
          className="ml-4 text-xl font-extrabold tracking-wide notice-content" 
          dangerouslySetInnerHTML={{ __html: notice.message }} 
        />
      </div>
      {notice.dismissible && (
        <button 
          onClick={handleDismiss} 
          className="ml-auto -mx-1.5 -my-1.5 rounded-lg focus:ring-2 focus:ring-gray-400 p-1.5 inline-flex h-8 w-8 notice-dismiss-btn"
          aria-label="Dismiss"
        >
          <span className="sr-only">Dismiss</span>
          <svg aria-hidden="true" className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default Notice;
